/**
 * Shift Utilities
 *
 * Shared utilities for shift type derivation and shift-related calculations.
 * Used by both backend and frontend code.
 */

/**
 * Shift type determines the icon and color styling
 */
export type ShiftType = 'morning' | 'afternoon' | 'night' | 'oncall';

/**
 * Derive shift type from shift name.
 * Optionally accepts a start hour to derive type from time.
 *
 * Detection priority:
 * 1. Keyword-based (highest priority) - checks name for keywords
 * 2. Time-based (if start hour provided and no keyword match)
 * 3. Default fallback (morning)
 *
 * @param name - Shift name to analyze
 * @param startHour - Optional start hour (0-23) for time-based detection
 * @returns ShiftType based on name keywords or start time
 */
export function deriveShiftType(name: string, startHour?: number): ShiftType {
  const lower = name.toLowerCase();

  // Check keywords in order of specificity
  if (lower.includes('oncall') || lower.includes('on-call') || lower.includes('pikett')) return 'oncall';

  // Night shifts
  if (lower.includes('night')) return 'night';
  if (lower.includes('closing')) return 'night';

  // Afternoon/Evening shifts
  if (lower.includes('afternoon')) return 'afternoon';
  if (lower.includes('evening')) return 'afternoon';

  // Morning shifts
  if (lower.includes('morning')) return 'morning';
  if (lower.includes('opening')) return 'morning';

  // If we have a start hour, derive from time
  if (startHour !== undefined) {
    if (startHour >= 5 && startHour < 12) return 'morning';
    if (startHour >= 12 && startHour < 18) return 'afternoon';
    return 'night'; // 18:00 onwards or before 5:00
  }

  return 'morning'; // default fallback
}

/** Icon mapping for shift types */
export const SHIFT_ICONS: Record<ShiftType, string> = {
  morning: 'wb_sunny',
  afternoon: 'wb_twilight',
  night: 'nightlight',
  oncall: 'phone_in_talk',
};
